import { rememberLogoutReason, type LogoutReason } from './logoutReason'

/**
 * The browser side of a Gateway session. localStorage is the only store, so every tab reads the same
 * session; this module adds the deadline, the timer, and the one notice that the session ended.
 *
 * Times are this browser's clock. Each limit starts when this browser saw the event, which is never
 * before the Gateway saw it, so the browser never ends a session that the Gateway still accepts.
 */
export type Session =
  | { status: 'anonymous' }
  | {
      status: 'active'
      token: string
      /** Epoch ms at which the token itself expires; null when the login did not say. */
      expiresAt: number | null
      /** The policy idle timeout in ms; null when the login did not say. */
      idleTimeout: number | null
      /** Epoch ms of the last request the Gateway accepted, in any tab. */
      activeAt: number | null
    }

const KEYS = {
  token: 'authToken',
  expiresAt: 'sessionExpiresAt',
  idleTimeout: 'sessionIdleTimeout',
  activeAt: 'sessionActiveAt',
} as const

const SESSION_DATA = [
  ...Object.values(KEYS),
  'accountId',
  'accountName',
  'loggedInUserName',
  'userId',
  'selectedSite',
]

// The Gateway keeps its activity stamp in whole seconds.
const GATEWAY_ROUNDING_MS = 1000
const LONGEST_TIMER_MS = 2 ** 31 - 1

const readNumber = (key: string) => {
  const value = Number(localStorage.getItem(key) ?? NaN)
  return Number.isFinite(value) ? value : null
}

export const readSession = (): Session => {
  const token = localStorage.getItem(KEYS.token)
  if (!token) return { status: 'anonymous' }
  return {
    status: 'active',
    token,
    expiresAt: readNumber(KEYS.expiresAt),
    idleTimeout: readNumber(KEYS.idleTimeout),
    activeAt: readNumber(KEYS.activeAt),
  }
}

type Ending = { at: number; reason: LogoutReason }

const ending = (session: Session): Ending | null => {
  if (session.status === 'anonymous') return null
  const idle =
    session.idleTimeout !== null && session.activeAt !== null
      ? {
          at: session.activeAt + session.idleTimeout + GATEWAY_ROUNDING_MS,
          reason: 'idle' as const,
        }
      : null
  const expiry =
    session.expiresAt !== null
      ? { at: session.expiresAt, reason: 'expired' as const }
      : null
  if (!idle) return expiry
  if (!expiry) return idle
  return idle.at <= expiry.at ? idle : expiry
}

/** Seconds, minutes and hours of an ISO-8601 duration as the Gateway writes it, such as PT30M. */
export const parseDuration = (value: unknown): number | null => {
  if (typeof value !== 'string') return null
  const match =
    /^PT(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?$/.exec(
      value
    )
  if (!match || value === 'PT') return null
  const [, hours = '0', minutes = '0', seconds = '0'] = match
  return ((Number(hours) * 60 + Number(minutes)) * 60 + Number(seconds)) * 1000
}

const listeners = new Set<() => void>()

/** Called once each time the active session of this tab ends, whatever ended it. */
export const onSessionEnd = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

let timer: ReturnType<typeof setTimeout> | undefined
let ended = readSession().status === 'anonymous'

const announceEnd = () => {
  clearTimeout(timer)
  if (ended) return
  ended = true
  listeners.forEach(listener => listener())
}

const schedule = () => {
  clearTimeout(timer)
  const next = ending(readSession())
  if (!next) return
  timer = setTimeout(
    checkSession,
    Math.min(Math.max(0, next.at - Date.now()), LONGEST_TIMER_MS)
  )
}

export const startSession = (
  token: string,
  limits: { ttl?: unknown; idleTimeout?: unknown } = {}
) => {
  const now = Date.now()
  const ttl = parseDuration(limits.ttl)
  const idleTimeout = parseDuration(limits.idleTimeout)
  localStorage.setItem(KEYS.token, token)
  localStorage.setItem(KEYS.activeAt, String(now))
  if (ttl === null) localStorage.removeItem(KEYS.expiresAt)
  else localStorage.setItem(KEYS.expiresAt, String(now + ttl))
  if (idleTimeout === null) localStorage.removeItem(KEYS.idleTimeout)
  else localStorage.setItem(KEYS.idleTimeout, String(idleTimeout))
  ended = false
  schedule()
}

/** Ends the session of every tab. Safe to call any number of times: only the first call announces the end. */
export const endSession = (reason?: LogoutReason) => {
  rememberLogoutReason(reason)
  SESSION_DATA.forEach(key => localStorage.removeItem(key))
  announceEnd()
}

/** Ends the session when one of its limits has passed. Returns whether the session is still active. */
export function checkSession(): boolean {
  const session = readSession()
  if (session.status === 'anonymous') {
    announceEnd()
    return false
  }
  const next = ending(session)
  if (next && Date.now() >= next.at) {
    endSession(next.reason)
    return false
  }
  ended = false
  schedule()
  return true
}

/** The Gateway accepted a request that carried this token, which restarts its idle clock. */
export const noteActivity = (token: string | undefined) => {
  if (!token || token !== localStorage.getItem(KEYS.token)) return
  localStorage.setItem(KEYS.activeAt, String(Date.now()))
  schedule()
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    if (event.storageArea !== null && event.storageArea !== localStorage) return
    if (event.key === null || event.key === KEYS.token) {
      if (!localStorage.getItem(KEYS.token)) {
        if (!ended) rememberLogoutReason('ended')
        announceEnd()
        return
      }
      ended = false
    }
    if (
      event.key === null ||
      (Object.values(KEYS) as string[]).includes(event.key)
    )
      schedule()
  })
  // A timer does not run while the computer sleeps, so check again when the user comes back.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkSession()
  })
  schedule()
}
